<template lang="pug">
v-container.home-wrap(fluid)
  //- Saludo
  .d-flex.justify-space-between.align-end.flex-wrap.mb-6
    div
      h1.home-hello Hola{{ firstName ? ', ' + firstName : '' }}
      .home-sub Configura tu negocio y descubre el potencial de tu mercado.
    v-btn.home-tour(text color="primary" @click="openWizard")
      v-icon.mr-1(size="20") mdi-compass-outline
      | Ver recorrido guiado

  v-row(align="stretch")
    //- Progreso
    v-col(cols="12" md="7")
      v-card.pa-6.card-flat(style="height:100%")
        .d-flex.justify-space-between.align-center.mb-2
          .card-title Configura tu negocio
          span.progress-num {{ completedSteps }} de 4
        v-progress-linear.mb-5(:value="completedSteps * 25" color="secondary" height="6" rounded background-color="#ECEDF0")
        .step-row(v-for="step in steps" :key="step.key" @click="$router.push(step.route)")
          v-icon(:color="step.done ? 'secondary' : 'grey lighten-1'" size="24") {{ step.done ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          .step-text
            .step-title {{ step.title }}
            .step-hint {{ step.hint }}
          v-icon.step-arrow(size="20" color="grey lighten-1") mdi-chevron-right

    //- Inicio rápido
    v-col(cols="12" md="5")
      v-card.pa-6.quick-card(dark style="height:100%")
        .card-title.white--text Empieza en un paso
        .quick-sub.mt-1 Sube un solo archivo con todo, o parte con un caso de ejemplo y edítalo.
        v-btn.mt-5(block large depressed color="white" @click="$refs.masterImportFormModal.openDialog()")
          v-icon.mr-2(color="primary") mdi-database-import
          span.primary--text.font-weight-medium Carga rápida
        v-btn.mt-3(block large outlined dark :loading="loadingExample" @click="loadExampleData")
          v-icon.mr-2 mdi-flask-outline
          | Partir con un ejemplo

  //- Carga por paquetes (ordenada y reutilizable)
  .section-label.mt-8.mb-2 Carga por paquetes
  .quick-load-text.mb-4
    | Carga en orden: 1) tu mercado (clientes), 2) tus productos, 3) los casos de consumo. Un mismo mercado te sirve para varios casos (posible vs real).
  v-row(align="stretch")
    v-col(cols="12" md="4")
      v-card.pa-5.card-flat.pkg-card(style="height:100%")
        .pkg-num 1
        .card-title.mt-2 Clientes y mercado
        .quick-load-text.mt-1 Tu cartera real o un paquete de empresas/industrias del mercado atendible (con su comuna).
        .d-flex.flex-wrap.mt-4(style="gap:8px")
          v-btn(small outlined color="primary" href="/plantilla-importar-clientes.xlsx" target="_blank" download)
            v-icon.mr-1(small) mdi-file-excel-outline
            | Plantilla
          v-btn(small depressed color="primary" @click="$refs.customerImportFormModal.openDialog()")
            v-icon.mr-1(small) mdi-upload
            | Subir
    v-col(cols="12" md="4")
      v-card.pa-5.card-flat.pkg-card(style="height:100%")
        .pkg-num 2
        .card-title.mt-2 Líneas, productos y capacidades
        .quick-load-text.mt-1 Tus líneas de negocio con sus productos o servicios (ticket y costo) y la capacidad.
        .d-flex.flex-wrap.mt-4(style="gap:8px")
          v-btn(small outlined color="primary" :href="prodTplHref" target="_blank" download)
            v-icon.mr-1(small) mdi-file-excel-outline
            | Plantilla
          v-btn(small depressed color="primary" @click="$refs.productFullImportFormModal.openDialog()")
            v-icon.mr-1(small) mdi-upload
            | Subir
    v-col(cols="12" md="4")
      v-card.pa-5.card-flat.pkg-card(style="height:100%")
        .pkg-num 3
        .card-title.mt-2 Casos de consumo (matriz)
        .quick-load-text.mt-1 Cuánto consume cada cliente de cada producto. Potencial (mercado atendible) vs Real (tu mercado).
        .d-flex.flex-wrap.mt-4(style="gap:8px")
          v-btn(small outlined color="primary" :loading="loadingCaseTpl" @click="downloadCaseTpl")
            v-icon.mr-1(small) mdi-file-excel-outline
            | Plantilla
          v-btn(small depressed color="primary" @click="$refs.caseMatrixModal.openDialog()")
            v-icon.mr-1(small) mdi-upload
            | Subir
  .quick-load-hint.mt-3
    | ¿Prefieres todo en un solo archivo? Usa la <strong>Carga rápida</strong> de arriba (crea todo y el caso de una).

  //- Accesos rápidos
  .section-label.mt-8.mb-3 Accesos rápidos
  v-row
    v-col(v-for="link in shortcuts" :key="link.title" cols="6" md="3")
      v-card.pa-5.shortcut-card.text-center(@click="$router.push(link.route)")
        v-icon(size="30" color="primary") {{ link.icon }}
        .shortcut-title.mt-3 {{ link.title }}

  MasterImportFormModal(ref="masterImportFormModal" @save="onDataLoaded")
  CustomerImportFormModal(ref="customerImportFormModal" @save="onDataLoaded")
  ProductFullImportFormModal(ref="productFullImportFormModal" :simple="isSimpleMode" @save="onDataLoaded")
  CaseMatrixImportFormModal(ref="caseMatrixModal" @save="onCaseCreated")
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { WIZARD_STEPS_EVENTS } from '@/constants'
import { globalEmitter } from '@/utils/eventBus'
import MasterImportFormModal from '@/components/commercial_projection/MasterImportFormModal.vue'
import CustomerImportFormModal from '@/components/customers/CustomerImportFormModal.vue'
import ProductFullImportFormModal from '@/components/products/ProductFullImportFormModal.vue'
import CaseMatrixImportFormModal from '@/components/commercial_projection/CaseMatrixImportFormModal.vue'

export default {
  name: 'Home',
  components: { MasterImportFormModal, CustomerImportFormModal, ProductFullImportFormModal, CaseMatrixImportFormModal },
  data() {
    return {
      loadingExample: false,
      loadingCaseTpl: false,
      counts: { customers: 0, products: 0, projections: 0, plans: 0 },
      shortcuts: [
        { title: 'Clientes', icon: 'mdi-account-group-outline', route: { name: 'Clientes' } },
        { title: 'Productos y servicios', icon: 'mdi-package-variant-closed', route: { name: 'Productos' } },
        { title: 'Casos y Mix Óptimo', icon: 'mdi-chart-box-outline', route: { name: 'Proyección Comercial' } },
        { title: 'Plan de ventas', icon: 'mdi-clipboard-text-outline', route: { name: 'Planes' } },
        { title: 'Seguimiento', icon: 'mdi-radar', route: { name: 'Seguimiento' } },
      ],
    }
  },
  computed: {
    ...mapGetters('app_config', ['currentBusiness']),
    isSimpleMode() {
      return this.currentBusiness && this.currentBusiness.catalog_mode === 'simplificado'
    },
    prodTplHref() {
      return this.isSimpleMode ? '/plantilla-productos-simple.xlsx' : '/plantilla-productos-completa.xlsx'
    },
    firstName() {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}')
        return (u.first_name || '').split(' ')[0] || ''
      } catch (e) {
        return ''
      }
    },
    steps() {
      return [
        { key: 'customers', title: '1. Clientes y mercado', hint: 'Tu cartera real o el mercado atendible', done: this.counts.customers > 0, route: { name: 'Clientes' } },
        { key: 'products', title: '2. Productos y servicios', hint: 'Líneas de negocio, ticket y costo', done: this.counts.products > 0, route: { name: 'Productos' } },
        { key: 'projections', title: '3. Modela un caso y ve tu Mix Óptimo', hint: 'Cuánto consume cada cliente: posible vs real', done: this.counts.projections > 0, route: { name: 'Proyección Comercial' } },
        { key: 'plans', title: '4. Plan de ventas', hint: 'Baja el Mix Óptimo a metas mensuales', done: this.counts.plans > 0, route: { name: 'Planes' } },
      ]
    },
    completedSteps() {
      return this.steps.filter(s => s.done).length
    },
  },
  methods: {
    ...mapActions('customer', ['listCustomers']),
    ...mapActions('product', ['listProducts']),
    ...mapActions('commercial_projection', ['listCommercialProjections', 'loadExample', 'downloadCaseTemplate']),
    ...mapActions('plan', ['listPlan']),
    openWizard() {
      globalEmitter(WIZARD_STEPS_EVENTS.START_WIZARD, 0)
    },
    _count(res) {
      const d = res && res.data
      if (!d) return 0
      if (typeof d.count === 'number') return d.count
      if (Array.isArray(d)) return d.length
      if (Array.isArray(d.results)) return d.results.length
      return 0
    },
    async fetchCounts() {
      const params = { page_size: 1 }
      const [cu, pr, pj] = await Promise.all([
        this.listCustomers({ params }).catch(() => null),
        this.listProducts({ params }).catch(() => null),
        this.listCommercialProjections({ params }).catch(() => null),
      ])
      this.counts.customers = this._count(cu)
      this.counts.products = this._count(pr)
      this.counts.projections = this._count(pj)
      try {
        const pl = await this.listPlan()
        this.counts.plans = this._count(pl)
      } catch (e) { /* opcional */ }
    },
    async loadExampleData() {
      this.loadingExample = true
      const res = await this.loadExample()
      this.loadingExample = false
      if (res && res.status >= 200 && res.status < 300) {
        this.$toast.success('Caso de ejemplo cargado. ¡Explóralo!')
        await this.fetchCounts()
        if (res.data && res.data.projection_id) {
          this.$router.push(`/proyeccion-comercial/${res.data.projection_id}/dashboard`)
        }
      } else {
        this.$toast.error('No se pudo cargar el ejemplo.')
      }
    },
    onDataLoaded() {
      this.fetchCounts()
    },
    async downloadCaseTpl() {
      this.loadingCaseTpl = true
      const res = await this.downloadCaseTemplate()
      this.loadingCaseTpl = false
      if (res && res.error) {
        this.$toast.error('No se pudo descargar la plantilla. Carga primero clientes y productos.')
      }
    },
    onCaseCreated(res) {
      this.fetchCounts()
      if (res && res.projection_id) {
        this.$router.push(`/proyeccion-comercial/${res.projection_id}/dashboard`)
      }
    },
  },
  mounted() {
    this.fetchCounts()
  },
}
</script>

<style scoped>
.home-wrap { max-width: 1100px; }
.home-hello { font-size: 1.7rem; font-weight: 600; color: #1B2E38; line-height: 1.2; }
.home-sub { color: #4B626E; margin-top: 4px; }
.home-tour { text-transform: none; letter-spacing: 0; }

.card-flat {
  border: 1px solid #ECEDF0;
  box-shadow: none !important;
}
.card-title { font-size: 1.1rem; font-weight: 600; color: #1B2E38; }
.progress-num { color: #4B626E; font-size: .9rem; }

.step-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-top: 1px solid #F1F2F5;
  cursor: pointer;
  transition: padding-left .15s;
}
.step-row:first-of-type { border-top: none; }
.step-row:hover { padding-left: 6px; }
.step-text { flex: 1; }
.step-title { font-weight: 500; color: #1B2E38; }
.step-hint { font-size: .8rem; color: #8A99A3; }
.step-arrow { opacity: .6; }

.quick-card { background: #5575DF !important; border-radius: 12px; }
.quick-sub { color: rgba(255,255,255,.85); font-size: .9rem; line-height: 1.5; }

.quick-load-text { color: #4B626E; font-size: .92rem; line-height: 1.5; }
.quick-load-hint { color: #8A99A3; font-size: .85rem; }

.pkg-card { position: relative; }
.pkg-num {
  width: 30px; height: 30px; border-radius: 50%;
  background: #5575DF; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-weight: 600; font-size: .95rem;
}

.section-label { font-size: .8rem; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: #8A99A3; }
.shortcut-card {
  border: 1px solid #ECEDF0;
  box-shadow: none !important;
  cursor: pointer;
  transition: transform .15s, border-color .15s, box-shadow .15s;
}
.shortcut-card:hover {
  transform: translateY(-2px);
  border-color: #5575DF;
  box-shadow: 0 4px 14px rgba(85,117,223,.12) !important;
}
.shortcut-title { font-weight: 500; color: #1B2E38; }
</style>
